import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import { stageContext } from '@src/globals';
import { setSecret } from '@store/reducer/clientReducer';
import store from '@store/store';

const defaultReconnectDelay = 10000;
const heartbeatInterval = 9 * 60 * 1000; // 9 Minuten in Millisekunden

export class WebSocketClient {
    static socket: WebSocket | null = null;
    static isConnecting: boolean = false;
    static lastSubscriptionId = 0;
    static subscriptions: { [topic: string]: { callback: CallableFunction; subKey: string }[] } = {};
    static reconnectDelay = 1000;
    static maxReconnectDelay = 60000;
    static pendingMessages: { topic: string; data: unknown }[] = [];
    static heartbeatIntervalId: NodeJS.Timeout | null = null;

    // Verbindung herstellen
    static async connect(): Promise<void> {
        if (WebSocketClient.socket || WebSocketClient.isConnecting) {
            return; // Verhindern, dass eine neue Verbindung erstellt wird, während bereits eine besteht oder aufgebaut wird
        }
        WebSocketClient.isConnecting = true;
        const state = store.getState();
        const secret = state.client.secret;
        if (!secret) {
            WebSocketClient.isConnecting = false;
            return;
        }
        const url = `${stageContext.apiWebsocketDomain}?auth=${encodeURIComponent(secret)}`;
        WebSocketClient.socket = new WebSocket(url);
        await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
                WebSocketClient.isConnecting = false;
                reject(new Error('Verbindungsaufbau zu WebSocket timeout.'));
            }, defaultReconnectDelay);

            if (!WebSocketClient.socket) {
                clearTimeout(timeout);
                WebSocketClient.isConnecting = false;
                reject(new Error('WebSocket-Instanz konnte nicht erstellt werden.'));
                return;
            }

            WebSocketClient.socket.onopen = () => {
                clearTimeout(timeout);
                WebSocketClient.isConnecting = false;
                this.reconnectDelay = defaultReconnectDelay;
                this.sendMessage('init', null);
                resolve();
                // Alle ausstehenden Nachrichten senden
                WebSocketClient.flushPendingMessages();
                // Starte Heartbeat
                WebSocketClient.startHeartbeat();
            };

            WebSocketClient.socket.onerror = (error) => {
                clearTimeout(timeout);
                WebSocketClient.isConnecting = false;
                console.error('WebSocket Fehler:', error);
                reject(error);
                WebSocketClient.reconnect();
            };

            WebSocketClient.socket.onclose = () => {
                WebSocketClient.isConnecting = false;
                console.warn('WebSocket Verbindung geschlossen. Versuche erneut zu verbinden...');
                WebSocketClient.stopHeartbeat();
                WebSocketClient.reconnect();
            };

            WebSocketClient.socket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    const topic = message.topic;
                    const data = message.data;
                    
                    // Debug logging für undefined topic
                    if (!topic) {
                        console.error('❌ WebSocket message with undefined topic:', message);
                        return;
                    }
                    
                    const subscriptions = WebSocketClient.subscriptions[topic];
                    // console.log('📨 WebSocket message received:', { topic, hasSubscriptions: !!subscriptions, subscriptionCount: subscriptions?.length || 0 });

                    let jsonData: unknown;
                    try {
                        // Prüfe ob data bereits ein Objekt ist oder ein JSON-String
                        if (typeof data === 'string') {
                            jsonData = JSON.parse(data);
                        } else {
                            jsonData = data;
                        }
                    } catch (parseError) {
                        console.error('❌ Failed to parse WebSocket data:', parseError, data);
                        return;
                    }

                    if (topic === EWebsocketTopics.refreshToken) {
                        console.log('New Login Token');
                        store.dispatch(setSecret((jsonData as { newSecret: string | null }).newSecret));
                    } else if (subscriptions) {
                        subscriptions.forEach((sub) => {
                            sub.callback(jsonData);
                        });
                    } else {
                        console.log(`Keine Subscriptions für ${topic}`);
                    }
                } catch (error) {
                    console.error('❌ WebSocket message processing error:', error);
                }
            };
        });
    }

    static async sendMessage(topic: string, data: unknown): Promise<void> {
        if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.CONNECTING)) {
            console.log('WebSocket ist noch nicht verbunden. Nachricht wird zur Warteschlange hinzugefügt...');
            WebSocketClient.pendingMessages.push({ topic, data });
            return;
        }

        try {
            if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
                console.log('WebSocket ist nicht verbunden. Verbindung wird hergestellt...');
                this.socket = null;
                await this.connect();
            }

            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ topic, data }));
            } else {
                this.socket = null;
                throw new Error('WebSocket-Verbindung konnte nicht hergestellt werden.');
            }
        } catch (error) {
            console.error('Fehler beim Senden der Nachricht:', error);
            WebSocketClient.pendingMessages.push({ topic, data });
        }
    }

    // Verbindung schließen
    static disconnect(): void {
        if (WebSocketClient.socket) {
            WebSocketClient.socket.close();
            WebSocketClient.socket = null;
        }
        WebSocketClient.stopHeartbeat();
    }

    static reconnect(): void {
        if (WebSocketClient.isConnecting) {
            return; // Verhindern, dass eine neue Verbindung initiiert wird, während bereits eine Verbindung hergestellt wird
        }
        setTimeout(() => {
            console.log('Versuche WebSocket erneut zu verbinden...');
            WebSocketClient.connect().catch((error) => {
                console.error('Fehler beim erneuten Verbinden:', error);
                WebSocketClient.reconnectDelay = Math.min(
                    WebSocketClient.reconnectDelay * 2,
                    WebSocketClient.maxReconnectDelay,
                );
                WebSocketClient.reconnect();
            });
        }, WebSocketClient.reconnectDelay);
    }

    static flushPendingMessages(): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            while (WebSocketClient.pendingMessages.length > 0) {
                const { topic, data } = WebSocketClient.pendingMessages.shift()!;
                this.socket.send(JSON.stringify({ topic, data }));
            }
        }
    }

    static startHeartbeat(): void {
        WebSocketClient.stopHeartbeat(); // Verhindern, dass mehrere Heartbeats laufen
        WebSocketClient.heartbeatIntervalId = setInterval(() => {
            if (WebSocketClient.socket && WebSocketClient.socket.readyState === WebSocket.OPEN) {
                WebSocketClient.sendMessage('heartbeat', null);
            }
        }, heartbeatInterval);
    }

    static stopHeartbeat(): void {
        if (WebSocketClient.heartbeatIntervalId) {
            clearInterval(WebSocketClient.heartbeatIntervalId);
            WebSocketClient.heartbeatIntervalId = null;
        }
    }

    /**
     * Unsubscribe from WebSocket topic
     * Removes subscription and cleans up empty topic arrays
     * @param subKey - Subscription key returned from subscribe()
     */
    static unsubscribe(subKey: string) {
        const [topic] = subKey.split('-');
        if (WebSocketClient.subscriptions[topic]) {
            WebSocketClient.subscriptions[topic] = WebSocketClient.subscriptions[topic].filter(
                (sub) => sub.subKey !== subKey,
            );

            // MEMORY LEAK PREVENTION: Clean up empty topic arrays
            // Without this: Empty arrays accumulate over time, causing memory bloat
            // With this: Clean slate for each topic, optimal memory usage
            if (WebSocketClient.subscriptions[topic].length === 0) {
                delete WebSocketClient.subscriptions[topic];
            }
        } else {
            console.warn('⚠️ Attempted to unsubscribe from non-existent topic:', topic, 'subKey:', subKey);
        }
    }

    /**
     * Subscribe to WebSocket topic with React Strict Mode protection
     *
     * CRITICAL TYPO FIX: Method was named "subscripe" causing widespread TypeScript errors
     * IMPACT: 20+ files failed compilation due to this single method name typo
     * SOLUTION: Renamed to correct "subscribe" spelling, fixed all compilation errors
     *
     * @param topic - WebSocket topic to subscribe to
     * @param callback - Function to call when messages are received
     * @returns Subscription key for unsubscribing
     */
    static subscribe<T>(topic: string, callback: (data: T) => void): string {
        const subKey = `${topic}-${WebSocketClient.lastSubscriptionId + 1}`;
        WebSocketClient.lastSubscriptionId = WebSocketClient.lastSubscriptionId + 1;

        if (!WebSocketClient.subscriptions[topic]) {
            WebSocketClient.subscriptions[topic] = [];
        }

        // CRITICAL: React Strict Mode duplicate subscription prevention
        // PROBLEM: React Strict Mode double-mounts cause duplicate subscriptions
        // SOLUTION: Replace existing subscription with same callback instead of adding new one
        // IMPACT: Prevents subscription chaos where responses go to wrong handlers
        const existingIndex = WebSocketClient.subscriptions[topic].findIndex((sub) => sub.callback === callback);
        if (existingIndex !== -1) {
            WebSocketClient.subscriptions[topic][existingIndex] = { subKey, callback };
        } else {
            WebSocketClient.subscriptions[topic].push({ subKey, callback });
        }
        return subKey;
    }
}
